import React, { useEffect, useMemo, useState } from "react";

import {

  Alert,

  Button,

  Form,

  FormGroup,

  Input,

  Label,

  Modal,

  ModalBody,

  ModalFooter,

  ModalHeader,

  Spinner,

} from "reactstrap";

import BorrowerSelect from "./BorrowerSelect";

import { fetchBorrowers, registerGuarantorWithDocuments } from "../../../api/borrowers";

import { fetchGuarantorRequirements } from "../../../api/guarantorSetup";

import {

  activeConsentRequirements,

  activeDocumentRequirements,

  activeProfileRequirements,

  profileFieldFormKey,

  validateGuarantorForm,

} from "../Settings/guarantorSettingsUtils";

import { runCrud } from "../Common/credoraToast";



const emptyForm = {

  borrower_id: "",

  full_name: "",

  phone: "",

  nida_number: "",

  consent_granted: false,

  liability_disclosure_ack: false,

};



const PROFILE_INPUT_PROPS = {

  full_name: { id: "guarantor-name", label: "Full name", type: "text" },

  phone: { id: "guarantor-phone", label: "Phone", type: "text" },

  nida: { id: "guarantor-nida", label: "National ID (NIDA)", type: "text" },

};

const GuarantorModal = ({ isOpen, toggle, onSaved, defaultBorrowerId = "" }) => {

  const [form, setForm] = useState({ ...emptyForm, borrower_id: defaultBorrowerId });

  const [documentFiles, setDocumentFiles] = useState({});

  const [borrowers, setBorrowers] = useState([]);

  const [requirements, setRequirements] = useState([]);

  const [loadingBorrowers, setLoadingBorrowers] = useState(false);

  const [loadingRequirements, setLoadingRequirements] = useState(false);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");



  useEffect(() => {

    if (!isOpen) {

      return;

    }

    setForm({ ...emptyForm, borrower_id: defaultBorrowerId });

    setDocumentFiles({});

    setError("");

    setLoadingBorrowers(true);

    setLoadingRequirements(true);

    fetchBorrowers(0, 200)

      .then(setBorrowers)

      .catch(() => setError("Failed to load borrowers."))

      .finally(() => setLoadingBorrowers(false));

    fetchGuarantorRequirements(false)

      .then(setRequirements)

      .catch(() => setError("Failed to load guarantor requirements."))

      .finally(() => setLoadingRequirements(false));

  }, [isOpen, defaultBorrowerId]);



  const profileRequirements = useMemo(() => activeProfileRequirements(requirements), [requirements]);

  const documentRequirements = useMemo(() => activeDocumentRequirements(requirements), [requirements]);

  const consentRequirements = useMemo(() => activeConsentRequirements(requirements), [requirements]);

  const updateField = (field, value) => {

    setForm((prev) => ({ ...prev, [field]: value }));

  };



  const updateDocumentFile = (documentType, file) => {

    setDocumentFiles((prev) => ({ ...prev, [documentType]: file || null }));

  };



  const handleSubmit = async (event) => {

    event.preventDefault();

    if (!form.borrower_id) {

      setError("Select a borrower.");

      return;

    }

    const missing = validateGuarantorForm(form, requirements, documentFiles);

    if (missing.length) {

      setError(`Missing required: ${missing.join(", ")}`);

      return;

    }

    setSaving(true);

    setError("");

    const uploads = documentRequirements

      .filter((req) => documentFiles[req.document_type])

      .map((req) => ({

        documentType: req.document_type,

        file: documentFiles[req.document_type],

      }));

    try {

      await runCrud(

        () =>

          registerGuarantorWithDocuments(

            form.borrower_id,

            {

              full_name: form.full_name.trim(),

              phone: form.phone.trim(),

              nida_number: form.nida_number.trim() || null,

              consent_granted: form.consent_granted,

              liability_disclosure_ack: form.liability_disclosure_ack,

            },

            uploads

          ),

        { successMessage: "Guarantor registered successfully." }

      );

      onSaved?.();

      toggle();

    } catch {

      /* error toast shown by runCrud */

    } finally {

      setSaving(false);

    }

  };



  const loading = loadingBorrowers || loadingRequirements;



  return (

    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="credora-guarantor-modal">

      <ModalHeader toggle={toggle}>Register Guarantor</ModalHeader>

      <Form onSubmit={handleSubmit}>

        <ModalBody>

          {error && <Alert color="danger">{error}</Alert>}

          {loading ? (

            <div className="text-center py-4">

              <Spinner size="sm" color="primary" />

            </div>

          ) : (

            <>

              <FormGroup>

                <Label for="guarantor-borrower">Borrower</Label>

                <BorrowerSelect
                  inputId="guarantor-borrower"
                  borrowers={borrowers}
                  value={form.borrower_id}
                  onChange={(nextId) => updateField("borrower_id", nextId)}
                  isDisabled={Boolean(defaultBorrowerId) || saving}
                />

              </FormGroup>



              {profileRequirements.map((req) => {

                const fieldKey = profileFieldFormKey(req.profile_field);

                const props = PROFILE_INPUT_PROPS[req.profile_field] || {

                  id: `guarantor-${req.profile_field}`,

                  label: req.label,

                  type: "text",

                };

                return (

                  <FormGroup key={req.id || req.key}>

                    <Label for={props.id}>

                      {req.label}

                      {req.is_required ? "" : " (optional)"}

                    </Label>

                    {req.description && <div className="text-muted small mb-1">{req.description}</div>}

                    <Input

                      id={props.id}

                      type={props.type}

                      value={form[fieldKey] || ""}

                      onChange={(e) => updateField(fieldKey, e.target.value)}

                      required={req.is_required}

                    />

                  </FormGroup>

                );

              })}



              {documentRequirements.length > 0 && (

                <div className="credora-guarantor-modal__consent-block mb-3">

                  <p className="credora-guarantor-modal__consent-title">Required documents</p>

                  {documentRequirements.map((req) => (

                    <FormGroup key={req.id || req.key}>

                      <Label for={`guarantor-doc-${req.document_type}`}>

                        {req.label}

                        {req.is_required ? "" : " (optional)"}

                      </Label>

                      {req.description && <div className="text-muted small mb-1">{req.description}</div>}

                      <Input

                        id={`guarantor-doc-${req.document_type}`}

                        type="file"

                        accept=".pdf,.jpg,.jpeg,.png,.webp"

                        onChange={(e) => updateDocumentFile(req.document_type, e.target.files?.[0] || null)}

                        required={req.is_required}

                      />

                    </FormGroup>

                  ))}

                </div>

              )}



              {consentRequirements.length > 0 && (

                <div className="credora-guarantor-modal__consent-block">

                  <p className="credora-guarantor-modal__consent-title">Compliance consents</p>

                  <p className="credora-guarantor-modal__consent-copy mb-0">

                    Confirm required consents before registering the guarantor.

                  </p>

                  {consentRequirements.map((req) => {

                    const isEConsent = req.consent_type === "e_consent";

                    const field = isEConsent ? "consent_granted" : "liability_disclosure_ack";

                    const inputId = isEConsent ? "guarantor-consent" : "guarantor-disclosure";

                    return (

                      <FormGroup check className="credora-guarantor-modal__consent-item" key={req.id || req.key}>

                        <Input

                          id={inputId}

                          type="checkbox"

                          checked={form[field]}

                          onChange={(e) => updateField(field, e.target.checked)}

                        />

                        <Label check for={inputId}>

                          {req.label}

                          {req.is_required ? "" : " (optional)"}

                        </Label>

                        {req.description && <div className="text-muted small ms-4">{req.description}</div>}

                      </FormGroup>

                    );

                  })}

                </div>

              )}

            </>

          )}

        </ModalBody>

        <ModalFooter>

          <Button color="light" className="border" onClick={toggle} disabled={saving}>

            Cancel

          </Button>

          <Button color="primary" type="submit" disabled={saving || loading}>

            {saving ? "Saving..." : "Register Guarantor"}

          </Button>

        </ModalFooter>

      </Form>

    </Modal>

  );

};



export default GuarantorModal;


